import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { cableId, type CableId } from './Cable.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.cable_event_ */
export type CableEventId = string & { __brand: 'CableEventId' }

/** Represents the table public.cable_event_ */
export default interface CableEventTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<CableEventId, CableEventId | undefined, CableEventId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  cableId: ColumnType<CableId, CableId, CableId>

  /** Database type: pg_catalog.text */
  action: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  notes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  operator: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  eventDate: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.int4 */
  calibrationId: ColumnType<number | null, number | null, number | null>
}

export type CableEvent = Selectable<CableEventTable>

export type CableEventInitializer = Insertable<CableEventTable>

export type CableEventMutator = Updateable<CableEventTable>

export const cableEventId = z.string() as unknown as z.Schema<CableEventId>

export const cableEvent = z.object({
  id: cableEventId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  cableId: cableId,
  action: z.string(),
  notes: z.string().nullable().optional(),
  operator: z.string().nullable().optional(),
  eventDate: z.coerce.date(),
  calibrationId: z.number().nullable().optional(),
}) as unknown as z.Schema<CableEvent>

export const cableEventInitializer = z.object({
  id: cableEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  cableId: cableId,
  action: z.string(),
  notes: z.string().optional().nullable(),
  operator: z.string().optional().nullable(),
  eventDate: z.coerce.date(),
  calibrationId: z.number().optional().nullable(),
}) as unknown as z.Schema<CableEventInitializer>

export const cableEventMutator = z.object({
  id: cableEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  cableId: cableId.optional(),
  action: z.string().optional(),
  notes: z.string().optional().nullable(),
  operator: z.string().optional().nullable(),
  eventDate: z.coerce.date().optional(),
  calibrationId: z.number().optional().nullable(),
}) as unknown as z.Schema<CableEventMutator>
